import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Feather, Menu, Phone, X } from "lucide-react";
import { scrollToId, scrollToTop } from "@/lib/scroll";
import { useSettings } from "@/context/SettingsContext";

const links = [
  { label: "Domestic", to: "/packages/domestic", testid: "nav-domestic-link" },
  { label: "International", to: "/packages/international", testid: "nav-international-link" },
  { label: "Visa Services", to: "/packages/visa", testid: "nav-visa-link" },
];

export const Navbar = () => {
  const settings = useSettings();
  const brandParts = settings.company_name.split(" ");
  const logoSrc = settings.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : null;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname === "/") {
      scrollToId(id);
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const goEnquire = () => goSection("enquiry");

  const goHome = (e) => {
    setOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      scrollToTop();
    } else {
      e.preventDefault();
      navigate("/", { state: { scrollTo: "__top" } });
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "backdrop-blur-xl bg-ink/85 border-b border-gold/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
        <Link to="/" onClick={goHome} data-testid="nav-logo" className="flex items-center gap-2.5 group">
          {logoSrc ? (
            <img src={logoSrc} alt={settings.company_name} className="h-11 w-auto object-contain bg-white rounded-xl px-2.5 py-1.5" />
          ) : (
            <span className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-ink transition-colors duration-300">
              <Feather size={17} strokeWidth={1.75} />
            </span>
          )}
          <span className="font-display text-base sm:text-lg tracking-wide text-cream leading-tight max-w-[190px] sm:max-w-[220px]">
            {brandParts[0]} <span className="italic text-gold">{brandParts.slice(1).join(" ")}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className="text-sm tracking-wide text-mist hover:text-gold transition-colors duration-200"
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={() => goSection("why")}
            data-testid="nav-why-us-link"
            className="text-sm tracking-wide text-mist hover:text-gold transition-colors duration-200"
          >
            Why Us
          </button>
          <button
            onClick={goEnquire}
            data-testid="nav-contact-link"
            className="text-sm tracking-wide text-mist hover:text-gold transition-colors duration-200"
          >
            Contact Us
          </button>
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href={`tel:${settings.phone.replace(/\s/g, "")}`}
            data-testid="nav-call-btn"
            className="flex items-center gap-2 text-xs font-mono tracking-widest text-mist hover:text-gold transition-colors"
          >
            <Phone size={14} /> {settings.phone}
          </a>
          <button
            onClick={goEnquire}
            data-testid="nav-enquire-btn"
            className="bg-gold text-ink text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-gold-hover transition-colors duration-200"
          >
            Enquire Now
          </button>
        </div>

        <button
          className="lg:hidden text-cream"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-menu-btn"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gold/10 bg-ink/95 backdrop-blur-xl px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`${l.testid}-mobile`}
              onClick={() => setOpen(false)}
              className="text-base text-mist hover:text-gold transition-colors"
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={goEnquire}
            data-testid="nav-enquire-btn-mobile"
            className="bg-gold text-ink text-sm font-semibold px-6 py-3 rounded-full w-max"
          >
            Enquire Now
          </button>
          <button
            onClick={() => goSection("why")}
            data-testid="nav-why-us-link-mobile"
            className="text-base text-mist hover:text-gold transition-colors text-left"
          >
            Why Us
          </button>
          <button
            onClick={goEnquire}
            data-testid="nav-contact-link-mobile"
            className="text-base text-mist hover:text-gold transition-colors text-left"
          >
            Contact Us
          </button>
        </div>
      )}
    </header>
  );
};
